import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
	deletePreFillableFormTemplate,
	editPreFillableFormTemplate,
	getDealsListData,
	getFileReactQuery,
	getPreFillableFormTemplateDetails,
} from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing.tsx';
import {
	CustomDetailCategory,
	PreFillableFormTemplateTypeEnum,
} from '@dealstack/shared/lib/generated';
import { usePreFillableFormOrganizationOptions } from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/common/usePreFillableFormOrganizationOptions.ts';

export enum EDIT_TEMPLATE_FORM_FIELDS {
	NAME = 'name',
	INSTRUCTION = 'instruction',
	TYPE = 'type',
	ASSIGNED_PROJECTS = 'assignedProjects',
	ASSIGNED_ORGANIZATIONS = 'assignedOrganizations',
	FILLABLE_FIELDS_MAPPING = 'fillableFieldsMapping',
	CATEGORY = 'category',
}

const templateFillableFieldShape = z.object({
	id: z.string(),
	fieldKey: z.string(),
	fieldSourceTemplate: z.string().nullish(),
	fieldSourceDescription: z.string().nullish(),
});

const formShape = z.object({
	[EDIT_TEMPLATE_FORM_FIELDS.NAME]: z.string().nonempty(),
	[EDIT_TEMPLATE_FORM_FIELDS.INSTRUCTION]: z.string().nullish(),
	[EDIT_TEMPLATE_FORM_FIELDS.TYPE]: z.nativeEnum(PreFillableFormTemplateTypeEnum),
	[EDIT_TEMPLATE_FORM_FIELDS.ASSIGNED_PROJECTS]: z.array(z.string()),
	[EDIT_TEMPLATE_FORM_FIELDS.ASSIGNED_ORGANIZATIONS]: z.array(z.string()),
	[EDIT_TEMPLATE_FORM_FIELDS.FILLABLE_FIELDS_MAPPING]: z.array(templateFillableFieldShape),
	[EDIT_TEMPLATE_FORM_FIELDS.CATEGORY]: z.nativeEnum(CustomDetailCategory).nullable().optional(),
});

export type EditPreFillableFormTemplateFormValues = z.infer<typeof formShape>;

export const useEditPreFillableFormController = () => {
	const { templateId } = useParams() as { templateId: string };
	const navigate = useNavigate();

	const getPreFillableFormDetailsRequest = useQuery({
		queryKey: [API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_ID_GET, { id: templateId }],
		queryFn: ({ signal }) =>
			getPreFillableFormTemplateDetails(
				API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_ID_GET,
				{ id: templateId },
				signal
			),
	});

	const [fileBlob, setFileBlob] = useState<Blob>();
	const [fileName, setFileName] = useState<string>();

	const fileId = getPreFillableFormDetailsRequest.data?.payload?.file?.id;
	const getFileQuery = useQuery({
		queryKey: [API_PATHS.GODMODE_FILE_ID_GET, { id: fileId ?? '' }],
		queryFn: ({ signal }) =>
			getFileReactQuery(API_PATHS.GODMODE_FILE_ID_GET, { id: fileId ?? '' }, signal),
		enabled: !!fileId,
	});

	useEffect(() => {
		if (!getFileQuery.isSuccess) {
			return;
		}
		void (async () => {
			const response = getFileQuery.data;
			if (!response) {
				return;
			}
			const blob = await response.blob();
			const name = response.headers.get('FileName') as string;
			setFileBlob(blob);
			setFileName(name);
		})();
	}, [getFileQuery.isSuccess, getFileQuery.data]);

	const getDealsRequest = useQuery({
		queryKey: [API_PATHS.GODMODE_DEALS_GET],
		queryFn: ({ signal }) => getDealsListData(API_PATHS.GODMODE_DEALS_GET, signal),
	});
	const editPreFillableTemplateRequest = useMutation({
		mutationKey: [API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_UPDATE_POST],
		mutationFn: editPreFillableFormTemplate,
	});
	const deletePreFillableFormTemplateRequest = useMutation({
		mutationFn: deletePreFillableFormTemplate,
	});

	const editPreFillableFormTemplateForm = useForm<EditPreFillableFormTemplateFormValues>({
		mode: 'all',
		resolver: zodResolver(formShape),
		defaultValues: {
			[EDIT_TEMPLATE_FORM_FIELDS.ASSIGNED_PROJECTS]: [],
			[EDIT_TEMPLATE_FORM_FIELDS.ASSIGNED_ORGANIZATIONS]: [],
			[EDIT_TEMPLATE_FORM_FIELDS.FILLABLE_FIELDS_MAPPING]: [],
		},
	});

	useEffect(() => {
		const templateData = getPreFillableFormDetailsRequest.data?.payload;
		if (!templateData) {
			return;
		}

		editPreFillableFormTemplateForm.reset({
			[EDIT_TEMPLATE_FORM_FIELDS.NAME]: templateData.name,
			[EDIT_TEMPLATE_FORM_FIELDS.INSTRUCTION]: templateData.instruction,
			[EDIT_TEMPLATE_FORM_FIELDS.TYPE]: templateData.type,
			[EDIT_TEMPLATE_FORM_FIELDS.CATEGORY]: templateData.category ?? undefined,
			[EDIT_TEMPLATE_FORM_FIELDS.ASSIGNED_PROJECTS]:
				templateData.preFillableFormTemplatesProjects.map(({ deal }) => deal.id),
			[EDIT_TEMPLATE_FORM_FIELDS.ASSIGNED_ORGANIZATIONS]:
				templateData.assignedOrganizationIds,
			[EDIT_TEMPLATE_FORM_FIELDS.FILLABLE_FIELDS_MAPPING]:
				templateData.preFillableFormTemplateFields.map((field) => ({
					id: field.id,
					fieldKey: field.fieldKey,
					fieldSourceTemplate: field.fieldSourceTemplate,
					fieldSourceDescription: field.fieldSourceDescription,
				})),
		});
	}, [getPreFillableFormDetailsRequest.data?.payload]);

	const onSubmit = (formValues: EditPreFillableFormTemplateFormValues) => {
		const templateData = getPreFillableFormDetailsRequest.data?.payload;
		if (!templateData) {
			return;
		}

		editPreFillableTemplateRequest.mutate(
			{
				id: templateData.id,
				name: formValues.name,
				instruction: formValues.instruction ?? '',
				type: formValues.type,
				category: formValues.category,
				assignedProjects: formValues.assignedProjects,
				assignedOrganizations: formValues.assignedOrganizations,
				fillableFieldsMapping: formValues.fillableFieldsMapping.map((field) => ({
					id: field.id,
					fieldKey: field.fieldKey,
					fieldSourceTemplate: field.fieldSourceTemplate || undefined,
					fieldSourceDescription: field.fieldSourceDescription || undefined,
				})),
			},
			{
				onSuccess: () => {
					showToastV2({ title: 'Updated successfully' });
					void getPreFillableFormDetailsRequest.refetch();
				},
			}
		);
	};

	const assignedProjectsOptions = useMemo(() => {
		if (!getDealsRequest.data?.payload) {
			return [];
		}
		return getDealsRequest.data.payload.map((deal) => ({ value: deal.id, label: deal.name }));
	}, [getDealsRequest.data?.payload]);

	const assignedOrganizationsOptions = usePreFillableFormOrganizationOptions();

	const [showDeletionDrawer, setShowDeletionDrawer] = useState(false);

	const onDeletionConfirm = () => {
		deletePreFillableFormTemplateRequest.mutate(templateId, {
			onSuccess: () => {
				showToastV2({ title: 'Deleted successfully' });
				navigate(GODMODE_ROOT_ROUTES.MANAGE_PRE_FILLABLE_FORMS);
			},
		});
	};

	return {
		navigate,
		editPreFillableFormTemplateForm,
		assignedProjectsOptions,
		assignedOrganizationsOptions,
		onSubmit,
		isFormSubmitting:
			editPreFillableFormTemplateForm.formState.isSubmitting ||
			editPreFillableTemplateRequest.isPending,
		isLoadingData: getPreFillableFormDetailsRequest.isFetching,
		fileName,
		fileBlob,
		formTemplateId: templateId,
		formTemplateType: getPreFillableFormDetailsRequest.data?.payload?.type,
		formTemplateData: getPreFillableFormDetailsRequest.data?.payload,
		showDeletionDrawer,
		setShowDeletionDrawer,
		deletePreFillableFormTemplateRequest,
		onDeletionConfirm,
	};
};
